// ============================================================================
// AdminMilestoneDefsPanel — the 16-cell milestone matrix (Build S5, Task #22).
// ============================================================================
// Sidebar: #admin/milestonedefs. David's ratification surface: each draft cell
// shows a badge; editing populates description/exit_criteria/evidence_required.
// Ratification (is_draft=false) is a deliberate opt-in via the "Ratified"
// checkbox — not an automatic side effect of editing (§12 escalation).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, Grid3x3, Pencil, Check } from 'lucide-react';
import { api, type MilestoneDef, type DocumentTypeVocab } from '../api';

const TRACK_LABELS: Record<string, string> = {
  CONS: 'Conservation', PROD: 'Production', FSAF: 'Food Safety', MKTG: 'Sales & Marketing',
};
const TRACK_ORDER = ['CONS', 'PROD', 'FSAF', 'MKTG'];

export function AdminMilestoneDefsPanel({ token }: { token: string }) {
  const [defs, setDefs] = useState<MilestoneDef[] | null>(null);
  const [docTypes, setDocTypes] = useState<DocumentTypeVocab[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      // Evidence codes are picked from the documents.document_type vocab (the
      // backend PATCH rejects codes absent/inactive there) — load both together.
      const [defsRes, dtRes] = await Promise.all([
        api.adminMilestoneDefs(token),
        api.adminDocumentTypes(token),
      ]);
      setDefs(defsRes.milestone_defs);
      setDocTypes(dtRes.document_types);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load milestone definitions');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4"><AlertCircle className="w-5 h-5 shrink-0" /> {error}</div>;
  }
  if (!defs) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  const draftCount = defs.filter((d) => d.is_draft).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Grid3x3 className="w-6 h-6 text-green-700" />
        <h2 className="text-xl font-bold text-gray-900">Milestone definitions</h2>
        <span className="text-sm text-gray-500">· {defs.length} cells · {draftCount} draft</span>
      </div>

      {TRACK_ORDER.map((tc) => {
        const cells = defs.filter((d) => d.track_code === tc).sort((a, b) => a.sort_order - b.sort_order);
        if (cells.length === 0) return null;
        return (
          <div key={tc} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="font-semibold text-gray-900">{TRACK_LABELS[tc] ?? tc} track</h3>
            <div className="mt-2 space-y-2">
              {cells.map((d) => (
                <DefRow
                  key={d.milestone_def_id}
                  token={token}
                  def={d}
                  docTypes={docTypes}
                  isEditing={editing === d.milestone_def_id}
                  onEdit={() => setEditing(d.milestone_def_id)}
                  onDone={async () => { setEditing(null); await load(); }}
                  onCancel={() => setEditing(null)}
                  setError={setError}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DefRow({
  token, def, docTypes, isEditing, onEdit, onDone, onCancel, setError,
}: {
  token: string;
  def: MilestoneDef;
  docTypes: DocumentTypeVocab[];
  isEditing: boolean;
  onEdit: () => void;
  onDone: () => Promise<void>;
  onCancel: () => void;
  setError: (s: string | null) => void;
}) {
  const [description, setDescription] = useState(def.description ?? '');
  const [exitCriteria, setExitCriteria] = useState(def.exit_criteria ?? '');
  const [evidence, setEvidence] = useState<Set<string>>(new Set(def.evidence_required ?? []));
  const [isDraft, setIsDraft] = useState(def.is_draft);
  const [busy, setBusy] = useState(false);

  // DefRow stays mounted across view/edit (keyed by id in the parent), so the
  // useState initializers above only run once. Re-sync the form to the latest
  // def every time the editor opens, so a prior cancel (unsaved edits) or save
  // (server-updated values) doesn't leave stale text in the fields.
  useEffect(() => {
    if (!isEditing) return;
    setDescription(def.description ?? '');
    setExitCriteria(def.exit_criteria ?? '');
    setEvidence(new Set(def.evidence_required ?? []));
    setIsDraft(def.is_draft);
  }, [isEditing, def]);

  const toggleEvidence = (code: string) => {
    setEvidence((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const save = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const evidenceList = Array.from(evidence).sort();  // deterministic order → stable diffs/persistence
      await api.adminMilestoneDefPatch(token, def.milestone_def_id, {
        description: description.trim() || null,
        exit_criteria: exitCriteria.trim() || null,
        evidence_required: evidenceList.length > 0 ? evidenceList : null,
        is_draft: isDraft,
      });
      await onDone();
    } catch (err: any) {
      setError(err?.message ?? 'Save failed');
    } finally {
      // Always clear busy — the row stays mounted, so leaving it set would
      // permanently disable Save on the next edit of this same cell.
      setBusy(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="flex items-start justify-between gap-2 border border-gray-100 rounded-lg p-2.5">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-gray-400">{def.stage_code.replace(/_/g, ' ')}</span>
            <span className="text-sm font-medium text-gray-800">{def.label}</span>
            {def.is_draft
              ? <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">draft</span>
              : <span className="text-[10px] uppercase tracking-wide text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">ratified</span>}
            {def.requires_cpa52 && <span className="text-[10px] text-gray-500">CPA-52</span>}
          </div>
          {def.description && <p className="mt-1 text-xs text-gray-600">{def.description}</p>}
          {(def.evidence_required?.length ?? 0) > 0 && (
            <p className="mt-0.5 text-xs text-gray-400">evidence: {def.evidence_required?.join(', ')}</p>
          )}
        </div>
        <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900 shrink-0">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div className="border border-green-200 bg-green-50/30 rounded-lg p-3 space-y-2">
      <div className="text-sm font-medium text-gray-800">{def.label}</div>
      <label className="block text-xs text-gray-600">Description
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1 text-sm" />
      </label>
      <label className="block text-xs text-gray-600">Exit criteria (what "done" means)
        <textarea value={exitCriteria} onChange={(e) => setExitCriteria(e.target.value)} rows={2} className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1 text-sm" />
      </label>
      <div className="text-xs text-gray-600">
        <span className="block mb-1">Evidence required (document types)</span>
        <div className="flex flex-wrap gap-1.5">
          {(() => {
            // Vocab from documents.document_type, plus any code already on this
            // def that is no longer in the vocab (shown as "unlisted" so a save
            // never silently drops it — the admin can deselect it deliberately).
            const known = new Set(docTypes.map((d) => d.type_code));
            const options: DocumentTypeVocab[] = [
              ...docTypes,
              ...Array.from(evidence).filter((c) => !known.has(c)).map((c) => ({ type_code: c, label: `${c} (unlisted)` })),
            ];
            if (options.length === 0) return <span className="text-gray-400 italic">No document types defined.</span>;
            return options.map((dt) => {
              const on = evidence.has(dt.type_code);
              return (
                <button
                  key={dt.type_code}
                  type="button"
                  onClick={() => toggleEvidence(dt.type_code)}
                  title={dt.label}
                  aria-pressed={on}
                  aria-label={`${dt.label} (${dt.type_code})`}
                  className={
                    'text-[11px] px-2 py-1 rounded-full border ' +
                    (on ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300 hover:border-green-600')
                  }
                >
                  {on && '✓ '}{dt.type_code}
                </button>
              );
            });
          })()}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={!isDraft} onChange={(e) => setIsDraft(!e.target.checked)} /> Ratified (visible to producers)
      </label>
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy} onClick={() => void save()} className="inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50">
          <Check className="w-3.5 h-3.5" /> Save
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500">Cancel</button>
      </div>
    </div>
  );
}
